import { useCallback, useEffect, useState } from 'react';
import AdressFelder from '../person/AdressFelder';
import { WizardSection } from '../ui/wizard-layout';
import {
  fehlendeAnmeldefelder,
  kundeAlsFormular,
  stammdatenApi,
  type KundenTreffer,
  type StrukturierteAdresse,
} from '../../lib/api/stammdaten';

/**
 * Die Anschrift des Kunden, wo sie für die Anmeldung noch fehlt (T-0320).
 *
 * Für die Probestunde reichte ein Kontaktweg, die Anmeldung braucht die
 * Anschrift — sie steht auf Vertrag, Mandat und Beleg. Bisher merkte man das
 * erst beim Anlegen am Ende des Assistenten, als Fehlermeldung des Servers.
 * Jetzt fragt die Übersicht sie gleich mit ab.
 *
 * ``kundenRef`` ist die Personennummer oder die Portal-Kennung des Kontos.
 */
export function useAnmeldeAnschrift(aktiv: boolean, kundenRef: number) {
  const [kunde, setKunde] = useState<KundenTreffer | null>(null);
  const [adresse, setAdresse] = useState<StrukturierteAdresse>({});

  useEffect(() => {
    setKunde(null);
    setAdresse({});
    if (!aktiv || kundenRef <= 0) return;
    let aktuell = true;
    stammdatenApi
      .kundeZuKontakt({ personId: kundenRef })
      .then((antwort) => {
        if (!aktuell || !antwort.kunde) return;
        setKunde(antwort.kunde);
        setAdresse(antwort.kunde.structuredAddress || {});
      })
      // Ohne Antwort bleibt es beim Prüfen durch den Server beim Anlegen.
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [aktiv, kundenRef]);

  const abfragen = kunde !== null && fehlendeAnmeldefelder(kunde.structuredAddress).length > 0;
  const fehlend = abfragen ? fehlendeAnmeldefelder(adresse) : [];

  /** Schreibt die Anschrift an den Kunden. ``false``, solange etwas fehlt. */
  const speichern = useCallback(async (): Promise<boolean> => {
    if (!abfragen || kunde === null) return true;
    if (fehlendeAnmeldefelder(adresse).length > 0) return false;
    // Der ganze Kunde geht mit: Mit nur der Anschrift setzte der Server den
    // Anzeigenamen eines Firmenkunden auf den Personennamen zurück.
    await stammdatenApi.sichern({
      personId: kunde.personId,
      kunde: { ...kundeAlsFormular(kunde), structuredAddress: adresse },
      profilWahl: 'konto',
    });
    setKunde({ ...kunde, structuredAddress: adresse });
    return true;
  }, [abfragen, adresse, kunde]);

  return { abfragen, adresse, setAdresse, fehlend, speichern };
}

type Props = Readonly<{
  adresse: StrukturierteAdresse;
  onChange: (adresse: StrukturierteAdresse) => void;
  fehlend: string[];
}>;

export default function AnmeldeAnschrift({ adresse, onChange, fehlend }: Props) {
  return (
    <WizardSection title="Anschrift des Kunden">
      <p className="mb-3 text-sm text-muted-foreground">
        Für die Anmeldung Pflicht — sie steht auf Vertrag, Mandat und Beleg. Bei der Probestunde reichte noch
        ein Kontaktweg.
      </p>
      <AdressFelder wert={adresse} onChange={onChange} fehlend={fehlend} />
    </WizardSection>
  );
}
